import React, { useState, forwardRef, useRef, useLayoutEffect } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { getStatusBadge } from "./ClaimsUtils";
import { NursingHomeClaim } from "@/types/admin";
import { isPendingClaimStatus } from "@/lib/claim-status";
import { FixedSizeList as List } from 'react-window';

interface ClaimsTableProps {
  claims: NursingHomeClaim[];
  activeTab: string;
  onViewDetails: (claim: NursingHomeClaim) => void;
}

// Custom TableBody wrapper for react-window
const VirtualizedTableBody = forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  (props, ref) => <tbody ref={ref} {...props} />
);

/**
 * Table of claims filtered by status.
 */
const ClaimsTable: React.FC<ClaimsTableProps> = ({ claims, activeTab, onViewDetails }) => {
  const filtered = claims.filter((claim) => {
    if (activeTab === "pending") return isPendingClaimStatus(claim.status);
    return claim.status === activeTab;
  });
  // Pagination state
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginatedClaims = filtered.slice((page - 1) * pageSize, page * pageSize);

  // Responsive width for virtualization
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useLayoutEffect(() => {
    function updateWidth() {
      if (tableContainerRef.current) {
        setContainerWidth(tableContainerRef.current.offsetWidth);
      }
    }
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);


  return (
    <div className="space-y-8 w-full min-w-[600px]">
      <div ref={tableContainerRef} className="w-full">
        <Table className="w-full">
          <TableHeader>
            <TableRow>
              <TableHead>Nursing Home</TableHead>
              <TableHead>Claimant</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Action</TableHead>
            </TableRow>
          </TableHeader>
          {paginatedClaims.length > 20 && containerWidth > 0 ? (
            <List
              height={Math.min(pageSize, paginatedClaims.length) * 56}
              itemCount={paginatedClaims.length}
              itemSize={56}
              width={containerWidth}
              outerElementType={VirtualizedTableBody}
            >
              {({ index, style }) => {
                const claim = paginatedClaims[index];
                return (
                  <TableRow key={claim.id} style={{ ...style, display: 'table-row' }}>
                    <TableCell className="font-medium">
                      {claim.nursing_home?.center_name || claim.nursing_home_id}
                      <div className="text-xs text-muted-foreground">
                        {claim.nursing_home?.county}
                      </div>
                    </TableCell>
                    <TableCell>
                      {(claim.user?.user_metadata?.first_name || "") + " " + (claim.user?.user_metadata?.last_name || "")}
                      <div className="text-xs text-muted-foreground">
                        {claim.user?.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      {claim.verification_documents?.role || "Not specified"}
                    </TableCell>
                    <TableCell>
                      {claim.created_at ? new Date(claim.created_at).toLocaleDateString() : ""}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(claim.status)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onViewDetails(claim)}
                      >
                        View Details
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              }}
            </List>
          ) : (
            <TableBody>
              {paginatedClaims.map(claim => (
                <TableRow key={claim.id}>
                  <TableCell className="font-medium">
                    {claim.nursing_home?.center_name || claim.nursing_home_id}
                    <div className="text-xs text-muted-foreground">
                      {claim.nursing_home?.county}
                    </div>
                  </TableCell>
                  <TableCell>
                    {(claim.user?.user_metadata?.first_name || "") + " " + (claim.user?.user_metadata?.last_name || "")}
                    <div className="text-xs text-muted-foreground">
                      {claim.user?.email}
                    </div>
                  </TableCell>
                  <TableCell>
                    {claim.verification_documents?.role || "Not specified"}
                  </TableCell>
                  <TableCell>
                    {claim.created_at ? new Date(claim.created_at).toLocaleDateString() : ""}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(claim.status)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onViewDetails(claim)}
                    >
                      View Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-2">
            <button
              className="px-2 py-1 border rounded disabled:opacity-50"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
            >
              Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`px-2 py-1 border rounded ${page === i + 1 ? 'bg-muted font-bold' : ''}`}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="px-2 py-1 border rounded disabled:opacity-50"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimsTable;
